import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Joyride, { STATUS, LIFECYCLE } from "react-joyride";
import store from "../../state/store";
import {
  getApiSchemaVersion,
  getWizardStatus,
} from "../../state/selectors/general.selectors";
import { setShowingWizard } from "../../state/actions/WizardActions";
import localStorageActions from "../helpers/localStorageActions";
import { styles, getSteps, getMaxVersion } from "./constants";
import apiClient from "../../services/apiClient";

const Wizard = ({ apiSchemaVersion, wizardEnabled }) => {
  if (!wizardEnabled) {
    // feature guard, don't show wizard if backend says so.
    return null;
  }
  const [showWizard, setShowWizard] = useState(false);
  const [highestAPIViewed, setHighestAPIViewed] = useState("0");
  const [stepsToShow, setStepsToShow] = useState([]);
  const [wizardDebug] = useState(false);

  useEffect(() => {
    if (wizardDebug) {
      setStepsToShow(getSteps("0.0"));
    } else {
      let highestViewedAPI = "";
      const lsApiViewed = localStorageActions.getHelpTourVersion();
      if (!lsApiViewed) {
        // no local storage show steps from v10 and after (end of 2021 after ClinVar submitions)
        highestViewedAPI = "10";
      }
      if (lsApiViewed) {
        highestViewedAPI = lsApiViewed;
      }
      setStepsToShow(getSteps(highestViewedAPI));
    }
  }, [apiSchemaVersion]);

  useEffect(() => {
    const apiList = [];
    stepsToShow.forEach((step) => {
      apiList.push(step.apiVersion);
    });
    if (apiList && apiList.length > 0) {
      setHighestAPIViewed(getMaxVersion(apiList));
    } else {
      setHighestAPIViewed(apiSchemaVersion);
    }
    if (stepsToShow && stepsToShow.length && !showWizard) {
      // wait for any ajax/async functions to end before showing the wizard
      setTimeout(() => {
        setShowWizard(true);
      }, 5000);
    }
  }, [stepsToShow]);

  const markWizardasViewed = () => {
    setShowWizard(false);
    store.dispatch(setShowingWizard(false));
    localStorageActions.setHelpTourVersion(highestAPIViewed);
    const apiPayload = {
      version: highestAPIViewed,
    };
    apiClient.wizardViewed(apiPayload).subscribe(
      () => {},
      (e) => {
        console.error("error saving wizard viewed version! error =>", e);
      }
    );
  };

  const handleJoyrideCallback = (data) => {
    const { status, lifecycle, type, step } = data;
    if (lifecycle === LIFECYCLE.INIT && status === STATUS.WAITING && step) {
      // wizard init
      store.dispatch(setShowingWizard(true));
    }
    if (lifecycle === LIFECYCLE.INIT && type === "tour:end") {
      // User finished or skipped the wizard
      markWizardasViewed();
    }
  };

  useEffect(() => {
    // If wizard is disabled wait for 3 seconds.
    // If useEffect that checks for existing steps
    // runs setShowWizard(true) this useEffect will be
    // cancelled and will re-run once we finish with the wizard;

    const timeoutId = setTimeout(() => {
      if (!showWizard) {
        store.dispatch(setShowingWizard(false));
      }
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [showWizard]);

  return (
    <>
      {stepsToShow && (
        <Joyride
          callback={handleJoyrideCallback}
          continuous={true}
          run={showWizard}
          scrollToFirstStep={true}
          showProgress={false}
          showSkipButton={true}
          debug={false}
          disableScrolling={true}
          steps={stepsToShow}
          styles={{
            options: styles,
          }}
        />
      )}
    </>
  );
};

Wizard.propTypes = {
  apiSchemaVersion: PropTypes.string,
  wizardEnabled: PropTypes.bool,
};

Wizard.defaultProps = {
  apiSchemaVersion: "",
  wizardEnabled: false,
};

const mapStateToProps = (state) => ({
  apiSchemaVersion: getApiSchemaVersion(state),
  wizardEnabled: getWizardStatus(state),
});

export default connect(mapStateToProps)(Wizard);
